import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/Common/Modal';
import { Button } from '@/components/Common/button';
import kbService, { KBStructure, DomainInfo, UseCaseInfo, CadrageInfo, CustomFolderInfo } from '@/services/kbService';
import notify from '@/lib/notify';
import { ChevronDown, ChevronRight, ClipboardList, FileText, FolderOpen, LayoutGrid, Loader2 } from 'lucide-react';
interface FolderSelectionModalProps {
  open: boolean;
  onClose: () => void;
  selectedPaths: string[];
  onSave: (paths: string[]) => void;
}

type FolderType = 'domain' | 'use_case' | 'cadrage' | 'custom';

interface FolderNode {
  id: string;
  name: string;
  path: string;
  type: FolderType;
  count: number;
  children?: FolderNode[];
}

export const FolderSelectionModal: React.FC<FolderSelectionModalProps> = ({
  open,
  onClose,
  selectedPaths,
  onSave,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [structure, setStructure] = useState<KBStructure | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set(selectedPaths));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      loadStructure();
      setSelected(new Set(selectedPaths));
      // Auto-expand custom group when modal opens
      setExpanded(new Set(['custom-group']));
    }
  }, [open, selectedPaths]);

  const loadStructure = async () => {
    setLoading(true);
    try {
      const data = await kbService.getStructure();
      setStructure(data);
    } catch (error) {
      console.error('Error loading KB structure:', error);
      notify.error(t('chatbots.folderSelection.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const buildTree = (): FolderNode[] => {
    if (!structure) return [];

    const nodes: FolderNode[] = [];

    // Build business structure
    structure.business.forEach((domain: DomainInfo) => {
      const domainNode: FolderNode = {
        id: `domain-${domain.id}`,
        name: domain.name,
        path: `domain:${domain.id}`,
        type: 'domain',
        count: domain.document_count,
        children: [],
      };

      domain.use_cases.forEach((useCase: UseCaseInfo) => {
        const useCaseNode: FolderNode = {
          id: `use_case-${useCase.id}`,
          name: useCase.name,
          path: `use_case:${useCase.id}`,
          type: 'use_case',
          count: useCase.document_count,
          children: [],
        };

        useCase.cadrages.forEach((cadrage: CadrageInfo) => {
          const cadrageNode: FolderNode = {
            id: `cadrage-${cadrage.id}`,
            name: cadrage.name,
            path: `cadrage:${cadrage.id}`,
            type: 'cadrage',
            count: cadrage.document_count,
          };
          useCaseNode.children!.push(cadrageNode);
        });

        if (useCaseNode.children!.length > 0) {
          domainNode.children!.push(useCaseNode);
        }
      });

      if (domainNode.children!.length > 0) {
        nodes.push(domainNode);
      }
    });

    // Build custom folders structure - grouped under a "Personnalisé" section
    if (structure.custom.length > 0) {
      const customGroupNode: FolderNode = {
        id: 'custom-group',
        name: t('knowledgeBasePage.customFolders.title'),
        path: 'custom-group', // Non-selectable
        type: 'custom',
        count: structure.custom.reduce((sum, f) => sum + f.document_count, 0),
        children: structure.custom.map((folder: CustomFolderInfo) => ({
          id: `custom-${folder.id}`,
          name: folder.name,
          path: `custom:${folder.id}`,
          type: 'custom',
          count: folder.document_count,
        })),
      };
      nodes.push(customGroupNode);
    }

    return nodes;
  };

  const toggleExpand = (nodeId: string) => {
    const newExpanded = new Set(expanded);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpanded(newExpanded);
  };

  const getAllChildPaths = (node: FolderNode): string[] => {
    const paths: string[] = [];
    if (node.path !== 'custom-group') {
      paths.push(node.path);
    }
    if (node.children) {
      node.children.forEach(child => {
        paths.push(...getAllChildPaths(child));
      });
    }
    return paths;
  };

  const toggleSelect = (path: string, node?: FolderNode) => {
    const newSelected = new Set(selected);

    if (path === 'custom-group') {
      // Ignore selection of custom group header
      return;
    }

    if (newSelected.has(path)) {
      // Deselect this node and all children
      newSelected.delete(path);
      if (node?.children) {
        const childPaths = getAllChildPaths(node);
        childPaths.forEach(p => newSelected.delete(p));
      }
    } else {
      // Select this node and all children
      newSelected.add(path);
      if (node?.children) {
        const childPaths = getAllChildPaths(node);
        childPaths.forEach(p => newSelected.add(p));
      }
    }
    setSelected(newSelected);
  };

  const handleSave = () => {
    onSave(Array.from(selected));
    onClose();
  };

  const getIconForType = (type: FolderType) => {
    if (type === 'domain') {
      return <LayoutGrid className="w-4 h-4 text-blue-500 flex-shrink-0" />;
    } else if (type === 'use_case') {
      return <ClipboardList className="w-4 h-4 text-green-500 flex-shrink-0" />;
    } else if (type === 'cadrage') {
      return <FileText className="w-4 h-4 text-orange-500 flex-shrink-0" />;
    } else if (type === 'custom') {
      return <FolderOpen className="w-4 h-4 text-amber-500 flex-shrink-0" />;
    }
    return null;
  };

  const renderNode = (node: FolderNode, level: number = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expanded.has(node.id);
    const isSelected = selected.has(node.path);
    const isGroupHeader = node.path === 'custom-group';

    return (
      <div key={node.id} className="select-none">
        <div
          className={`flex items-center gap-2 py-2 px-3 rounded transition-colors ${
            isGroupHeader
              ? 'cursor-default font-semibold bg-muted/30'
              : 'hover:bg-muted/50 cursor-pointer'
          } ${
            isSelected && !isGroupHeader ? 'bg-primary/10 border-l-2 border-primary' : ''
          }`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
        >
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(node.id);
              }}
              className="w-4 h-4 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          )}

          {!hasChildren && <div className="w-4" />}

          {!isGroupHeader && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleSelect(node.path, node)}
              className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          {isGroupHeader && <div className="w-4" />}

          <div
            className="flex-1 flex items-center gap-2"
            onClick={() => !isGroupHeader && hasChildren ? toggleExpand(node.id) : undefined}
          >
            {getIconForType(node.type)}
            <span className={`text-sm ${isGroupHeader ? 'font-semibold' : 'font-medium'}`}>{node.name}</span>
            <span className="text-xs text-muted-foreground">({node.count})</span>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div>
            {node.children!.map((child) => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const tree = buildTree();

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="max-w-3xl"
      bodyClassName="max-h-[600px] overflow-y-auto"
      hideDefaultFooter
    >
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold">{t('chatbots.folderSelection.title')}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('chatbots.folderSelection.description')}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : tree.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t('chatbots.folderSelection.empty')}</p>
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="max-h-[400px] overflow-y-auto p-2">
              {tree.map((node) => renderNode(node))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {t('chatbots.folderSelection.selectedCount', { count: selected.size })}
          </div>
          <div className="flex gap-2">
            <Button type="button" onClick={onClose} variant="outline">
              {t('common.cancel')}
            </Button>
            <Button type="button" onClick={handleSave}>
              {t('chatbots.folderSelection.confirm')}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
